import React from 'react';
import { UserManager, WebStorageStateStore } from 'oidc-client-ts';
import type { User as OidcUser } from 'oidc-client-ts';
import { store } from '../store';
import { loginSuccess, logout } from '../store/slices/authSlice';
import { oidcConfig } from '../config/featureFlags';
import type { PlatformRole } from '../types/Team.types';
import { isLogoutInProgress } from '../utils/Auth.util';

const claimsToUser = (profile: Record<string, any>) => ({
  id: profile.sub as string,
  email: (profile.email ?? profile.preferred_username ?? '') as string,
  name: (profile.name ?? profile.preferred_username ?? '') as string,
  role: (profile.platform_role ?? 'USER') as PlatformRole,
});

/**
 * Single UserManager instance shared across the whole app.
 * Using oidc-client-ts directly (without react-oidc-context's AuthProvider)
 * avoids the infinite re-render bug that occurs with React 19.
 */
export const userManager = new UserManager({
  authority: oidcConfig.authority,
  client_id: oidcConfig.clientId,
  redirect_uri: oidcConfig.redirectUri,
  post_logout_redirect_uri: oidcConfig.postLogoutRedirectUri,
  scope: oidcConfig.scope,
  response_type: 'code',
  automaticSilentRenew: true,
  userStore: new WebStorageStateStore({ store: window.sessionStorage }),
});

// ---- Redux sync via UserManager events (outside React render cycle) ----
// This is the key: event handlers run outside React, so they never cause
// the infinite re-render loops that AuthProvider/userManagerContext has.

userManager.events.addUserLoaded((user: OidcUser) => {
  if (isLogoutInProgress()) {
    return;
  }

  store.dispatch(loginSuccess(claimsToUser(user.profile)));
});

userManager.events.addUserUnloaded(() => {
  store.dispatch(logout());
});

userManager.events.addSilentRenewError((error) => {
  console.warn('[OIDC] Silent renew error:', error);
});

// Restore persisted session on page load / hard refresh
userManager.getUser().then((user) => {
  if (isLogoutInProgress()) {
    return;
  }

  if (user && !user.expired) {
    store.dispatch(loginSuccess(claimsToUser(user.profile)));
  }
});

interface OidcAuthProviderProps {
  children: React.ReactNode;
}

/**
 * Thin wrapper — provides no extra React context, just renders children.
 * All OIDC logic (UserManager, Redux sync) lives at module level above.
 * The actual callback handling is in OidcCallbackPage (/auth/callback).
 */
export const OidcAuthProvider: React.FC<OidcAuthProviderProps> = ({
  children,
}) => {
  return <>{children}</>;
};
